/**
 * Componente Card
 *
 * Contenedor visual con variantes para diferentes contextos.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/core/utils';
import type { Layer } from '@/core/types';
import { getLayerColors } from '@/core/constants';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined' | 'glass' | Layer;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  interactive?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
}

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

const variantStyles: Record<string, string> = {
  default: 'bg-slate-900 border border-slate-800',
  elevated: 'bg-slate-900 border border-slate-800 shadow-lg shadow-black/20',
  outlined: 'bg-transparent border border-slate-700',
  glass: 'bg-slate-900/50 backdrop-blur-md border border-slate-800/50',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      interactive = false,
      header,
      footer,
      className,
      children,
      ...props
    },
    ref
  ) => {
    // Determinar si es una variante de capa
    const isLayerVariant = ['survival', 'maintenance', 'growth'].includes(variant);

    let cardClasses = variantStyles[variant] ?? variantStyles['default'];
    let accentClasses = '';

    if (isLayerVariant) {
      const colors = getLayerColors(variant as Layer);
      cardClasses = 'bg-slate-900 border border-slate-800';
      accentClasses = `border-l-2 ${colors.border}`;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl overflow-hidden',
          cardClasses,
          accentClasses,
          interactive && 'cursor-pointer hover:border-slate-700 transition-colors',
          className
        )}
        {...props}
      >
        {header && (
          <div className="px-4 py-3 border-b border-slate-800">{header}</div>
        )}

        <div className={paddingStyles[padding]}>{children}</div>

        {footer && (
          <div className="px-4 py-3 border-t border-slate-800 bg-slate-900/50">
            {footer}
          </div>
        )}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Subcomponentes para composición
 */
export function CardHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center justify-between gap-4', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('text-lg font-semibold text-slate-100', className)}
      {...props}
    >
      {children}
    </h3>
  );
}

export function CardDescription({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-sm text-slate-400', className)} {...props}>
      {children}
    </p>
  );
}

export function CardContent({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('', className)} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center justify-end gap-2', className)}
      {...props}
    >
      {children}
    </div>
  );
}
